import { styled } from "goober";
import { useRef, type ReactNode } from "react";
import { useScrollFade } from "../../hooks/useScrollFade";
import { ConnectorLogo } from "../ConnectorLogo";
import { GhostPill } from "../GhostPill";
import { PressButton } from "../PressButton";
import { Text } from "../Text";
import { formatRequestCount } from "./connectorTraffic";
import {
  Bar,
  BarColumn,
  BarRow,
  barColumnStyles,
  ColumnMeta,
  Columns,
  logBarFraction,
} from "./monitoringBars";
import { Panel, PanelHeader } from "./workflowsLayout";

export type ConnectorVolumeBar = {
  key: string;
  connectorId: string;
  identifier: string;
  label: string;
  requests: number;
  color: string;
};

// Grows to fill the panel, but holds a 64px min basis (shrink 0) so once there
// are more connectors than fit the row scrolls horizontally rather than
// compressing the bars.
const Column = styled(PressButton)`
  ${barColumnStyles}
  flex: 1 0 64px;
`;

// The bars' scroll container, mirroring Executions-by-space: soft faded edges
// driven by useScrollFade's --fade-left/--fade-right once the bars overflow.
// While a connector is selected the rest fade back (but a hovered one lifts to
// full so it stays clickable).
const ScrollColumns = styled(Columns)`
  overflow-x: auto;
  overflow-y: hidden;
  justify-content: flex-start;
  -webkit-mask-image: linear-gradient(
    to right,
    transparent,
    black var(--fade-left, 0px),
    black calc(100% - var(--fade-right, 0px)),
    transparent
  );
  mask-image: linear-gradient(
    to right,
    transparent,
    black var(--fade-left, 0px),
    black calc(100% - var(--fade-right, 0px)),
    transparent
  );

  &[data-has-selection] [data-bar-column]:not([data-selected]):not(:hover) {
    opacity: 0.4;
  }
`;

const LogoSlot = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 16px;
  margin-bottom: 6px;
`;

const GhostLogo = styled("div")`
  width: 16px;
  height: 16px;
  border-radius: 5px;
  background: var(--alpha8);
`;

// Descending sample bars — a ranked-by-requests silhouette for the empty state.
const GHOST_BARS = [
  { key: "g1", count: 1000, labelWidth: 44, countWidth: 26 },
  { key: "g2", count: 620, labelWidth: 52, countWidth: 22 },
  { key: "g3", count: 380, labelWidth: 40, countWidth: 24 },
  { key: "g4", count: 210, labelWidth: 48, countWidth: 20 },
  { key: "g5", count: 120, labelWidth: 36, countWidth: 18 },
  { key: "g6", count: 60, labelWidth: 44, countWidth: 16 },
];

export function ConnectorVolumeBars({
  bars,
  description,
  selectedConnectorId,
  onToggle,
  placeholder,
  action,
}: {
  bars: readonly ConnectorVolumeBar[];
  description: string;
  selectedConnectorId: string | null;
  onToggle: (connectorId: string) => void;
  placeholder?: boolean;
  action?: ReactNode;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  useScrollFade({ scrollRef });
  const maxRequests = Math.max(1, ...bars.map((bar) => bar.requests));

  if (placeholder) {
    return (
      <Panel style={{ minHeight: 0 }}>
        <PanelHeader>
          <Text variant="text-small-md">Requests by connector</Text>
          <Text variant="text-small" color="var(--text-secondary)">
            {description}
          </Text>
        </PanelHeader>
        <ScrollColumns ref={scrollRef} aria-hidden>
          {GHOST_BARS.map((bar) => (
            <BarColumn key={bar.key}>
              <BarRow>
                <Bar
                  style={{
                    width: 6,
                    background: "var(--alpha8)",
                    minHeight: 6,
                    height: `${logBarFraction(bar.count, GHOST_BARS[0]!.count) * 100}%`,
                  }}
                />
              </BarRow>
              <ColumnMeta>
                <LogoSlot>
                  <GhostLogo />
                </LogoSlot>
                <GhostPill style={{ width: bar.labelWidth }} />
                <GhostPill style={{ width: bar.countWidth }} />
              </ColumnMeta>
            </BarColumn>
          ))}
        </ScrollColumns>
        {action}
      </Panel>
    );
  }

  return (
    <Panel style={{ minHeight: 0 }}>
      <PanelHeader>
        <Text variant="text-small-md">Requests by connector</Text>
        <Text variant="text-small" color="var(--text-secondary)">
          {description}
        </Text>
      </PanelHeader>
      <ScrollColumns
        ref={scrollRef}
        data-has-selection={selectedConnectorId != null || undefined}
      >
        {bars.map((bar) => (
          <Column
            key={bar.key}
            data-bar-column
            data-selected={bar.connectorId === selectedConnectorId || undefined}
            onPress={() => onToggle(bar.connectorId)}
          >
            <BarRow>
              <Bar
                data-bar
                style={{
                  background: bar.color,
                  minHeight: bar.requests > 0 ? 6 : 0,
                  height: `${logBarFraction(bar.requests, maxRequests) * 100}%`,
                }}
              />
            </BarRow>
            <ColumnMeta>
              <LogoSlot>
                <ConnectorLogo
                  template={{ name: bar.label, identifier: bar.identifier }}
                  size={16}
                />
              </LogoSlot>
              <Text variant="text-small" color="var(--text-secondary)" ellipsis>
                {bar.label}
              </Text>
              <Text variant="text-small-md" style={{ whiteSpace: "nowrap" }}>
                {formatRequestCount(bar.requests)}
              </Text>
            </ColumnMeta>
          </Column>
        ))}
      </ScrollColumns>
      {action}
    </Panel>
  );
}
